import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { Router, RouterLink } from '@angular/router'
import { injectParams } from 'ngxtension/inject-params'
import Keycloak from 'keycloak-js'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { lastValueFrom } from 'rxjs'
import { ButtonModule } from '@openng/optimus-ui/button'
import { Tag } from '@openng/optimus-ui/tag'
import { Skeleton } from '@openng/optimus-ui/skeleton'
import { GalleriaModule } from '@openng/optimus-ui/galleria'
import { ToastModule } from '@openng/optimus-ui/toast'
import { MessageService } from '@openng/optimus-ui/api'

import {
	EquipmentAdDetailDto,
	EquipmentCtaDetail,
	EquipmentSearchService,
} from '../../../core/equipment-search.service'
import { EquipmentAdService } from '../../../core/equipment-ad.service'
import { SecureTransactionService } from '../../../core/secure-transaction.service'
import { SeoService } from '../../../core/seo.service'
import { MarketplaceReviewsSection } from '../../../shared/marketplace-reviews/marketplace-reviews-section'
import { environment } from '../../../../environments/environment'
import EquipmentCard from '../equipment-page/equipment-card'
import {
	AUTHENTICITY_LABELS,
	conditionLabel,
	DELIVERY_METHOD_LABELS,
	equipmentMediaUrl,
	hasFlaw,
	RETURN_BEARER_LABELS,
	SALE_KIND_LABELS,
	SHIPPING_BEARER_LABELS,
	SPECIAL_USE_LABELS,
} from '../equipment-page/equipment-labels'

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/** Canonical SEO origin — must match SeoService (kept in sync by convention). */
const SITE_BASE_URL = 'https://beautyboss.ir'

interface SpecRow {
	label: string
	value: string | null
}

/**
 * برچسب فیلدهای داینامیک شناخته‌شده. فیلدهای عمومی (دیکشنری دسته) label خود را
 * از سرور می‌گیرند؛ این نقشه برای فیلدهای «متریال» و «مناسب برای» است که در
 * dynamicValues ذخیره می‌شوند (TKT-44694 / TKT-52053) و تا وقتی ادمین فیلد
 * داینامیک متناظر را نساخته، نام کلید خام نمایش داده نشود.
 */
const DYNAMIC_FIELD_LABELS: Record<string, string> = {
	suitable_for: 'مناسب برای',
	material_volume: 'حجم',
	material_weight: 'وزن',
	material_unit: 'واحد',
	material_expiry_date: 'تاریخ انقضا',
	material_usage_type: 'نوع مصرف',
	material_usage_notes: 'نحوه استفاده',
	material_attributes: 'ویژگی محصول',
}

/** ترجمه مقادیر کدمانندِ فیلدهای داینامیک شناخته‌شده. */
const DYNAMIC_VALUE_LABELS: Record<string, Record<string, string>> = {
	suitable_for: {
		SALON: 'سالن زیبایی',
		CLINIC: 'کلینیک زیبایی',
		BOTH: 'سالن و کلینیک',
	},
}

/** «مناسب برای» وقتی به‌صورت فیلد داینامیک ذخیره شده باشد (سلامت‌محور، فقط اگر موجود بود). */
function suitableForValue(values: Record<string, unknown> | null | undefined): string | null {
	const raw = values?.['suitable_for']
	if (raw === null || raw === undefined || raw === '') return null
	return DYNAMIC_VALUE_LABELS['suitable_for'][String(raw)] ?? String(raw)
}

@Component({
	selector: 'app-equipment-details',
	standalone: true,
	imports: [
		CommonModule,
		RouterLink,
		Tag,
		Skeleton,
		GalleriaModule,
		ButtonModule,
		ToastModule,
		EquipmentCard,
		MarketplaceReviewsSection,
	],
	providers: [MessageService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div dir="rtl" class="min-h-screen bg-slate-50 pb-24 md:pb-10">
			<div class="max-w-6xl mx-auto px-4 py-8">
				@if (query.isPending()) {
					<div class="bg-white rounded-3xl border border-slate-100 p-8">
						<p-skeleton height="14rem" styleClass="rounded-2xl mb-5" />
						<p-skeleton height="2rem" width="50%" class="mb-3" />
						<p-skeleton height="1rem" width="80%" />
					</div>
				} @else if (query.isError()) {
					<div class="bg-white rounded-3xl border border-slate-100 p-12 text-center">
						<i class="pi pi-wrench text-4xl text-slate-300 mb-4 block"></i>
						<h1 class="text-xl font-black text-slate-700 mb-2">آگهی موردنظر یافت نشد</h1>
						<p class="text-sm text-slate-500 mb-6">ممکن است حذف شده یا لینک اشتباه باشد.</p>
						<a
							routerLink="/equipment"
							class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-bold"
							>همه تجهیزات</a
						>
					</div>
				} @else if (ad(); as c) {
					<div class="grid lg:grid-cols-3 gap-6">
						<!-- Main column -->
						<div class="lg:col-span-2 space-y-6">
							<!-- Hero: gallery + title + price + CTAs -->
							<div class="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
								@if (media().length) {
									<p-galleria
										[value]="media()"
										[showThumbnails]="media().length > 1"
										[showItemNavigators]="media().length > 1"
										[circular]="true"
										styleClass="course-galleria">
										<ng-template let-item pTemplate="item">
											<img
												[src]="item"
												[alt]="c.title ?? ''"
												class="w-full aspect-[16/9] object-cover"
												style="display: block" />
										</ng-template>
									</p-galleria>
								} @else {
									<div
										class="w-full aspect-[16/9] bg-gradient-to-l from-purple-100 to-pink-50 flex items-center justify-center">
										<i class="pi pi-wrench text-5xl text-purple-300"></i>
									</div>
								}
								<div class="p-6 md:p-8">
									<div class="flex items-center gap-2 flex-wrap mb-3">
										@if (condition(c)) {
											<p-tag [value]="conditionLabel(condition(c))" [rounded]="true" severity="secondary" />
										}
										@if (isSecure(c)) {
											<p-tag [rounded]="true" value="پرداخت امن" severity="success" />
										}
										@if (isNegotiable(c)) {
											<p-tag [rounded]="true" value="قابل مذاکره" severity="warn" />
										}
										@if (isWholesale(c)) {
											<p-tag [rounded]="true" value="فروش عمده" severity="info" />
										}
										@if (suitableForLabel(); as suitableFor) {
											<p-tag [rounded]="true" [value]="'مناسب برای: ' + suitableFor" severity="info" />
										}
									</div>
									<h1 class="text-lg md:text-2xl font-black text-slate-800 leading-9">{{ c.title }}</h1>
									@if (c.shortDescription) {
										<p class="text-sm text-slate-500 mt-3 leading-7">{{ c.shortDescription }}</p>
									}
									<div class="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-xs md:text-sm text-slate-500">
										@if (c.cityName) {
											<span class="inline-flex items-center gap-1"
												><i class="pi pi-map-marker text-purple-400"></i>{{ c.cityName }}</span
											>
										}
										@if (brandModel(); as bm) {
											<span class="inline-flex items-center gap-1"
												><i class="pi pi-tag text-purple-400"></i>{{ bm }}</span
											>
										}
										@if (c.viewCount != null) {
											<span class="inline-flex items-center gap-1"
												><i class="pi pi-eye text-purple-400"></i>{{ c.viewCount.toLocaleString('fa-IR') }} بازدید</span
											>
										}
									</div>
								</div>
							</div>

							@if (quickSpecs().length) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-4">مشخصات کلی</h2>
									<div class="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
										@for (row of quickSpecs(); track row.label) {
											<div class="flex gap-2 bg-slate-50 rounded-xl px-4 py-2.5">
												<span class="font-bold text-slate-400 shrink-0 text-xs mt-0.5">{{ row.label }}:</span>
												<span class="text-slate-700 leading-7">{{ row.value }}</span>
											</div>
										}
									</div>
								</section>
							}

							@if (dynamicRows().length) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-4">مشخصات</h2>
									<div class="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
										@for (row of dynamicRows(); track row.label) {
											<div class="flex gap-2 bg-slate-50 rounded-xl px-4 py-2.5">
												<span class="font-bold text-slate-400 shrink-0 text-xs mt-0.5">{{ row.label }}:</span>
												<span class="text-slate-700 leading-7">{{ row.value }}</span>
											</div>
										}
									</div>
								</section>
							}

							@if (hasFlaw(condition(c)) && c.conditionFlawDesc) {
								<section class="bg-white rounded-3xl border border-amber-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3 flex items-center gap-2"
										><i class="pi pi-exclamation-triangle text-amber-500"></i>ایرادات</h2
									>
									<p class="text-sm leading-8 text-slate-600 whitespace-pre-line">{{ c.conditionFlawDesc }}</p>
								</section>
							}

							@if (c.accessories) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3">لوازم همراه</h2>
									<p class="text-sm leading-8 text-slate-600 whitespace-pre-line">{{ c.accessories }}</p>
									@if (includedItems().length) {
										<div class="flex flex-wrap gap-2 mt-3">
											@for (item of includedItems(); track item) {
												<span class="bg-slate-50 text-slate-600 text-xs font-bold rounded-lg px-3 py-1.5">{{
													item
												}}</span>
											}
										</div>
									}
								</section>
							} @else if (includedItems().length) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3">لوازم همراه</h2>
									<div class="flex flex-wrap gap-2">
										@for (item of includedItems(); track item) {
											<span class="bg-slate-50 text-slate-600 text-xs font-bold rounded-lg px-3 py-1.5">{{
												item
											}}</span>
										}
									</div>
								</section>
							}

							@if (authenticityLabel()) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3 flex items-center gap-2"
										><i class="pi pi-verified text-emerald-500"></i>اصالت کالا</h2
									>
									<p class="text-xs text-slate-400 mb-2">اعلام‌شده توسط فروشنده</p>
									<p class="text-sm text-slate-700 font-bold">{{ authenticityLabel() }}</p>
									@if (c.supplierName) {
										<p class="text-xs text-slate-500 mt-2">تأمین‌کننده: {{ c.supplierName }}</p>
									}
								</section>
							}

							@if (specialUseLabel()) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3">شرایط تخصصی</h2>
									<p class="text-xs text-slate-400 mb-2">اعلام‌شده توسط فروشنده</p>
									<p class="text-sm text-slate-700 font-bold">{{ specialUseLabel() }}</p>
									@if (c.specialUseDesc) {
										<p class="text-sm leading-7 text-slate-600 mt-2 whitespace-pre-line">{{ c.specialUseDesc }}</p>
									}
								</section>
							}

							@if (shippingRows().length || deliveryMethodLabels()) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-4">ارسال</h2>
									<div class="text-sm space-y-2">
										@if (deliveryMethodLabels()) {
											<div class="flex justify-between gap-4"
												><span class="text-slate-400">روش‌های تحویل</span
												><b class="text-slate-700 text-left">{{ deliveryMethodLabels() }}</b></div
											>
										}
										@for (row of shippingRows(); track row.label) {
											<div class="flex justify-between gap-4"
												><span class="text-slate-400">{{ row.label }}</span
												><b class="text-slate-700">{{ row.value }}</b></div
											>
										}
									</div>
								</section>
							}

							@if (wantsSwap()) {
								<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
									<h2 class="text-base font-black text-slate-800 mb-3 flex items-center gap-2"
										><i class="pi pi-arrows-h text-purple-500"></i>معاوضه</h2
									>
									<div class="text-sm space-y-2">
										<span
											class="inline-flex items-center gap-1.5 rounded-full bg-purple-50 text-purple-600 text-xs font-bold px-3 py-1.5">
											<i class="pi pi-arrows-h"></i> این فروشنده مایل به معاوضه هست
										</span>
										@if (c.swapFor) {
											<div class="flex justify-between gap-4"
												><span class="text-slate-400">پیشنهاد معاوضه</span
												><b class="text-slate-700">{{ c.swapFor }}</b></div
											>
										}
										@if (c.swapNotes) {
											<p class="text-sm leading-7 text-slate-600 pt-2 whitespace-pre-line">{{ c.swapNotes }}</p>
										}
									</div>
								</section>
							}

							<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
								<h2 class="text-base font-black text-slate-800 mb-3 flex items-center gap-2"
									><i class="pi pi-replay text-purple-500"></i>مرجوعی</h2
								>
								@if (c.acceptsReturn) {
									<div class="text-sm space-y-2">
										<div class="flex justify-between gap-4"
											><span class="text-slate-400">پذیرش مرجوعی</span><b class="text-emerald-600">می‌پذیرد</b></div
										>
										@if (c.returnWindowDays != null) {
											<div class="flex justify-between gap-4"
												><span class="text-slate-400">مهلت</span
												><b class="text-slate-700">{{ c.returnWindowDays.toLocaleString('fa-IR') }} روز</b></div
											>
										}
										@if (c.returnCostBearer) {
											<div class="flex justify-between gap-4"
												><span class="text-slate-400">هزینه برگشت با</span
												><b class="text-slate-700">{{ returnBearerLabel(c.returnCostBearer) }}</b></div
											>
										}
										@if (c.returnConditions) {
											<p class="text-sm leading-7 text-slate-600 pt-2 whitespace-pre-line">{{ c.returnConditions }}</p>
										}
									</div>
								} @else {
									<p class="text-sm text-slate-500">این فروشنده مرجوعی نمی‌پذیرد.</p>
								}
							</section>

							<!-- Marketplace M7: real reviews (verified buyers of this seller) -->
							@if (sellerKcUserId(); as sid) {
								<app-marketplace-reviews-section [sellerKcUserId]="sid" [adId]="adId()" />
							}

							@if (similar().length) {
								<section>
									<h2 class="text-base font-black text-slate-800 mb-4">تجهیزات مشابه</h2>
									<div class="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
										@for (doc of similar(); track doc.id) {
											<app-equipment-card [doc]="doc" [showFavorite]="false" [inCompare]="false" />
										}
									</div>
								</section>
							}
						</div>

						<!-- Sidebar -->
						<aside class="space-y-6">
							<div class="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm lg:sticky lg:top-6">
								@if (displayPrice(); as price) {
									<p class="text-2xl font-black text-slate-800 inline"
										>{{ price }} <span class="text-sm font-bold text-slate-400">تومان</span></p
									>
								} @else {
									<p class="text-xl font-black text-slate-800">تماس برای قیمت</p>
								}
								@if (isNegotiable(c)) {
									<p class="text-[11px] text-slate-400 mt-1">قابل مذاکره</p>
								}

								<!-- Marketplace M5: escrow CTA is the primary action on secure-payment ads. -->
								@if (isSecure(c)) {
									<button (click)="goCheckout()" pButton class="w-full mt-3">
										<i class="pi pi-shield ml-1"></i> خرید با پرداخت امن
									</button>
									<p class="text-[10px] text-slate-400 mt-2 leading-5"
										>مبلغ تا تأیید دریافت شما نزد بیوتی‌باس امانت می‌ماند.</p
									>
								}

								@if (primaryCta(); as cta) {
									@if (ctaHref(cta); as href) {
										<a [href]="href" target="_blank" rel="noopener" pButton class="w-full">
											<i class="pi pi-arrow-left ml-1"></i> {{ ctaLabel(cta) }}
										</a>
									} @else {
										<button (click)="onCta(cta)" pButton class="w-full">
											<i class="pi pi-arrow-left ml-1"></i> {{ ctaLabel(cta) }}
										</button>
									}
								}
								@if (secondaryCtas().length) {
									<div class="flex flex-wrap gap-2 mt-2">
										@for (cta of secondaryCtas(); track cta.id ?? cta.type) {
											@if (ctaHref(cta); as href) {
												<a [href]="href" [text]="true" target="_blank" rel="noopener" pButton size="small">{{
													ctaLabel(cta)
												}}</a>
											} @else {
												<button [text]="true" (click)="onCta(cta)" pButton size="small">{{ ctaLabel(cta) }}</button>
											}
										}
									</div>
								}
							</div>

							<div class="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
								<h2 class="text-sm font-black text-slate-800 mb-3">فروشنده</h2>
								<div class="flex items-center gap-3">
									<div class="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center"
										><i class="pi pi-user text-purple-400"></i
									></div>
									<div>
										@if (sellerKcUserId(); as sid) {
											<!-- Marketplace M7: seller profile (badges + trust score + reviews) -->
											<a
												[routerLink]="['/sellers', sid]"
												class="font-bold text-slate-700 text-sm hover:text-purple-700"
												>{{ sellerName() }}</a
											>
											<p class="text-[11px] text-slate-400">بج‌ها، اعتمادسنجی و نظرات فروشنده</p>
										} @else {
											<p class="font-bold text-slate-700 text-sm">{{ sellerName() }}</p>
											<p class="text-[11px] text-slate-400">آگهی‌های این فروشنده به‌زودی…</p>
										}
									</div>
								</div>
								<button [text]="true" (click)="reportAbuse()" pButton size="small" class="w-full mt-4 !text-red-400">
									<i class="pi pi-flag ml-1"></i> گزارش تخلف
								</button>
							</div>
						</aside>
					</div>
				}
			</div>

			<!-- Sticky mobile price + CTA -->
			@if (ad(); as c) {
				<div
					class="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white/95 backdrop-blur border-t border-slate-100 px-4 py-3 flex items-center gap-3">
					<div class="flex-1">
						@if (displayPrice(); as price) {
							<p class="text-sm font-black text-slate-800">{{ price }} تومان</p>
						} @else {
							<p class="text-sm font-black text-slate-800">تماس برای قیمت</p>
						}
						<p class="text-[10px] text-slate-400">{{ conditionLabel(condition(c)) }}</p>
					</div>
					@if (isSecure(c)) {
						<button (click)="goCheckout()" pButton size="small"><i class="pi pi-shield ml-1"></i> خرید امن</button>
					} @else if (primaryCta(); as cta) {
						@if (ctaHref(cta); as href) {
							<a [href]="href" target="_blank" rel="noopener" pButton size="small">{{ ctaLabel(cta) }}</a>
						} @else {
							<button (click)="onCta(cta)" pButton size="small">{{ ctaLabel(cta) }}</button>
						}
					}
				</div>
			}
			<p-toast />
		</div>
	`,
})
export default class EquipmentDetails {
	private readonly searchService = inject(EquipmentSearchService)
	private readonly dictService = inject(EquipmentAdService)
	private readonly txService = inject(SecureTransactionService)
	private readonly seo = inject(SeoService)
	private readonly toast = inject(MessageService)
	private readonly keycloak = inject(Keycloak)
	private readonly router = inject(Router)

	readonly idOrSlug = injectParams('idSlug')
	readonly adId = computed(() => this.idOrSlug()?.match(UUID_RE)?.[0] ?? this.idOrSlug())

	readonly query = injectQuery(() => ({
		queryKey: ['equipment-public', this.adId()],
		queryFn: () => lastValueFrom(this.searchService.getOne(this.adId() ?? '')),
	}))

	readonly ad = computed(() => this.query.data())

	/**
	 * Marketplace M7 — the ad's seller. Direct field when the backend exposes
	 * it, else resolved from my own transactions on this ad (authenticated
	 * buyers/sellers). Guests on ads without the field see no reviews block
	 * (TODO(backend) — see EquipmentAdDetailDto.sellerKcUserId).
	 */
	private readonly myTxsForAdQuery = injectQuery(() => ({
		queryKey: ['mp-my-txs-for-ad', this.adId()],
		enabled: !!this.adId() && this.keycloak.authenticated === true,
		queryFn: () => lastValueFrom(this.txService.getMy({ page: 0, size: 50 })),
	}))

	readonly sellerKcUserId = computed<string | null>(() => {
		const direct = this.ad()?.sellerKcUserId
		if (direct) return direct
		const tx = (this.myTxsForAdQuery.data()?.content ?? []).find(t => t.adId === this.adId())
		return tx?.sellerKcUserId ?? null
	})

	// template-visible label helpers
	readonly hasFlaw = hasFlaw
	readonly conditionLabel = conditionLabel

	readonly media = computed(() =>
		(this.ad()?.mediaUrls ?? this.ad()?.mediaFileUrls ?? [])
			.map(u => equipmentMediaUrl(u))
			.filter((u): u is string => !!u)
	)

	readonly condition = (c: EquipmentAdDetailDto) => c.condition ?? c.equipmentCondition ?? null
	readonly isSecure = (c: EquipmentAdDetailDto) => (c.securePaymentEnabled ?? c.equipmentSecurePayment) === true
	readonly isNegotiable = (c: EquipmentAdDetailDto) => c.negotiable === true
	readonly isWholesale = (c: EquipmentAdDetailDto) =>
		c.equipmentWholesale === true || c.saleKind === 'WHOLESALE' || c.saleKind === 'BOTH'

	readonly brandModel = computed(() => {
		const c = this.ad()
		if (!c) return null
		const brand = c.equipmentBrandName ?? c.brandTitleResolved ?? c.brandTitle
		const model = c.equipmentModelName ?? c.modelTitle
		return [brand, model].filter(Boolean).join(' / ') || null
	})

	readonly displayPrice = computed(() => {
		const c = this.ad()
		if (!c || c.contactForPrice) return null
		const price = c.minEffectivePrice ?? c.price
		return price != null && price > 0 ? price.toLocaleString('fa-IR') : null
	})

	/* ── CTAs (primary first) ──────────────────────────────────────────── */

	readonly ctas = computed<EquipmentCtaDetail[]>(() => {
		const list = [...(this.ad()?.ctas ?? [])]
		list.sort(
			(a, b) => Number(b.primary ?? false) - Number(a.primary ?? false) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
		)
		return list.filter(c => c.type || c.value)
	})

	readonly primaryCta = computed(() => this.ctas()[0] ?? null)
	readonly secondaryCtas = computed(() => this.ctas().slice(1))

	ctaLabel(cta: EquipmentCtaDetail): string {
		switch (cta.type) {
			case 'WHATSAPP':
				return 'گفتگو در واتساپ'
			case 'PHONE':
				return 'تماس با فروشنده'
			case 'MESSAGE':
				return 'پیام به فروشنده'
			default:
				return 'تماس'
		}
	}

	/** Tracked link when the backend provides one, else a direct scheme link. */
	ctaHref(cta: EquipmentCtaDetail): string | null {
		if (cta.trackUrl) return `${environment.apiUrl}${cta.trackUrl}`
		const value = cta.value ?? ''
		if (!value) return null
		switch (cta.type) {
			case 'WHATSAPP':
				return `https://wa.me/${value.replace(/[^0-9]/g, '')}`
			case 'PHONE':
				return `tel:${value}`
			default:
				return null // MESSAGE → in-app chat lands with M6; handled by onCta
		}
	}

	onCta(cta: EquipmentCtaDetail) {
		if (cta.type === 'MESSAGE') {
			this.toast.add({
				severity: 'info',
				summary: 'پیام‌رسانی داخلی',
				detail: 'گفتگوی داخلی بیوتی‌باس به‌زودی فعال می‌شود.',
			})
			return
		}
		const href = this.ctaHref(cta)
		if (href) window.open(href, '_blank', 'noopener')
	}

	/**
	 * Marketplace M5: escrow checkout — guests go through Keycloak login first
	 * with a redirect straight back into checkout.
	 */
	goCheckout() {
		const id = this.adId()
		if (!id) return
		if (!this.keycloak.authenticated) {
			void this.keycloak.login({ redirectUri: window.location.origin + '/equipment/checkout/' + id })
			return
		}
		void this.router.navigate(['/equipment/checkout', id])
	}

	/* ── Specs sections ────────────────────────────────────────────────── */

	readonly quickSpecs = computed<SpecRow[]>(() => {
		const c = this.ad()
		if (!c) return []
		return [
			{ label: 'برند', value: c.equipmentBrandName ?? c.brandTitleResolved ?? c.brandTitle ?? null },
			{ label: 'مدل', value: c.equipmentModelName ?? c.modelTitle ?? null },
			{
				label: 'دسته',
				value:
					[c.equipmentCategoryName ?? c.categoryTitle, c.equipmentSubcategoryName ?? c.subcategoryTitle]
						.filter(Boolean)
						.join(' / ') || null,
			},
			{ label: 'وضعیت', value: this.condition(c) ? conditionLabel(this.condition(c)) : null },
			{ label: 'کشور سازنده', value: c.countryOfOrigin ?? null },
			{
				label: 'سال ساخت',
				value: c.manufactureYear != null ? c.manufactureYear.toLocaleString('fa-IR', { useGrouping: false }) : null,
			},
			{ label: 'موجودی', value: c.inventoryCount != null ? c.inventoryCount.toLocaleString('fa-IR') : null },
			{ label: 'نوع فروش', value: c.saleKind ? (SALE_KIND_LABELS[c.saleKind] ?? c.saleKind) : null },
			{
				label: 'مدت استفاده',
				value: c.usageDurationMonths != null ? `${c.usageDurationMonths.toLocaleString('fa-IR')} ماه` : null,
			},
			{ label: 'میزان کارکرد', value: c.usageMeter ?? null },
			{
				label: 'تعمیر',
				value:
					c.repaired ?
						c.repairDesc ?
							`تعمیر شده — ${c.repairDesc}`
						:	'تعمیر شده'
					:	null,
			},
			// serialNumber ممکن است null باشد → نمایش نده؛ خصوصی هم فقط برای پشتیبانی است.
			{ label: 'شماره سریال', value: c.serialNumber && !c.serialPrivate ? c.serialNumber : null },
		].filter((r): r is SpecRow => !!r.value)
	})

	private readonly dynamicFieldsQuery = injectQuery(() => ({
		queryKey: ['equipment-dict-dynamic-fields', this.ad()?.categoryId],
		enabled: !!this.ad()?.categoryId,
		queryFn: () => lastValueFrom(this.dictService.getDynamicFields(this.ad()!.categoryId!)),
	}))

	/** dynamicValues به‌عنوان «مشخصات» — labels resolved from the category dict. */
	readonly dynamicRows = computed<SpecRow[]>(() => {
		const c = this.ad()
		const values = c?.dynamicValues
		if (!values) return []
		const fields = this.dynamicFieldsQuery.data() ?? []
		return Object.entries(values)
			.filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
			.map(([key, v]) => ({
				label: fields.find(f => f.fieldKey === key)?.label ?? DYNAMIC_FIELD_LABELS[key] ?? key,
				value: this.dynamicValueLabel(key, v),
			}))
	})

	/** مقدار نمایشی یک فیلد داینامیک: کدها ترجمه و تاریخ انقضا شمسی می‌شود. */
	private dynamicValueLabel(key: string, value: unknown): string {
		if (Array.isArray(value)) return value.map(item => this.scalarLabel(key, item)).join('، ')
		if (typeof value === 'object' && value !== null) return JSON.stringify(value)
		return this.scalarLabel(key, value)
	}

	private scalarLabel(key: string, value: unknown): string {
		if (typeof value === 'boolean') return value ? 'بله' : 'خیر'
		const text = String(value)
		const mapped = DYNAMIC_VALUE_LABELS[key]?.[text]
		if (mapped) return mapped
		if (key === 'material_expiry_date' && /^\d{4}-\d{2}-\d{2}/.test(text)) {
			const date = new Date(text)
			if (!Number.isNaN(date.getTime())) return date.toLocaleDateString('fa-IR')
		}
		return text
	}

	/** «مناسب برای» (سالن/کلینیک/هر دو) — از ستون اختصاصی DTO و در نبود آن از dynamicValues. */
	readonly suitableForLabel = computed(() => {
		const field = this.ad()?.suitableFor
		if (field) return DYNAMIC_VALUE_LABELS['suitable_for'][String(field)] ?? String(field)
		return suitableForValue(this.ad()?.dynamicValues)
	})

	readonly includedItems = computed<string[]>(() => {
		const c = this.ad()
		if (!c) return []
		return [
			c.hasBox ? 'جعبه' : null,
			c.hasManual ? 'دفترچه راهنما' : null,
			c.hasInvoice ? 'فاکتور' : null,
			c.hasWarranty ?
				`گارانتی${c.warrantyRemainingMonths != null ? ` (${c.warrantyRemainingMonths.toLocaleString('fa-IR')} ماه باقی‌مانده)` : ''}`
			:	null,
			c.warrantyCard ? 'کارت گارانتی' : null,
		].filter((x): x is string => !!x)
	})

	readonly authenticityLabel = computed(() => {
		const a = this.ad()?.authenticity
		return a ? (AUTHENTICITY_LABELS[a] ?? a) : null
	})

	readonly specialUseLabel = computed(() => {
		const s = this.ad()?.specialUse
		return s ? (SPECIAL_USE_LABELS[s] ?? s) : null
	})

	readonly deliveryMethodLabels = computed(() => {
		const methods = this.ad()?.deliveryMethods ?? []
		return methods.map(m => DELIVERY_METHOD_LABELS[m] ?? m).join('، ') || null
	})

	readonly shippingRows = computed<SpecRow[]>(() => {
		const c = this.ad()
		if (!c) return []
		const rows: SpecRow[] = []
		if (c.shippingCostBearer) {
			const bearer = SHIPPING_BEARER_LABELS[c.shippingCostBearer] ?? c.shippingCostBearer
			rows.push({
				label: 'هزینه ارسال',
				value:
					c.shippingCostAmount != null && (c.shippingCostBearer === 'BUYER' || c.shippingCostBearer === 'SELLER') ?
						`${bearer} — ${c.shippingCostAmount.toLocaleString('fa-IR')} تومان`
					:	bearer,
			})
		}
		if (c.deliveryArea) rows.push({ label: 'محدوده تحویل', value: c.deliveryArea })
		return rows
	})

	readonly wantsSwap = computed(() => {
		const ad = this.ad()
		if (!ad) return false
		if (ad.isExchangeable === true) return true
		const m = ad.swapMode
		return m === 'ONLY_SWAP' || m === 'SALE_OR_SWAP'
	})

	returnBearerLabel(bearer: string): string {
		return RETURN_BEARER_LABELS[bearer] ?? bearer
	}

	readonly sellerName = computed(() => this.ad()?.sellerName ?? this.ad()?.supplierName ?? 'فروشنده')

	/* ── Similar items (same category, without self) ───────────────────── */

	private readonly similarQuery = injectQuery(() => ({
		queryKey: ['equipment-similar', this.ad()?.categoryId, this.adId()],
		enabled: !!this.ad()?.categoryId,
		queryFn: () =>
			lastValueFrom(this.searchService.searchEquipment({ equipmentCategoryId: this.ad()!.categoryId ?? null }, 0, 6)),
	}))

	readonly similar = computed(() =>
		(this.similarQuery.data()?.content ?? []).filter(d => d.id !== this.adId()).slice(0, 6)
	)

	/* ── Report abuse placeholder ──────────────────────────────────────── */

	reportAbuse() {
		this.toast.add({
			severity: 'success',
			summary: 'گزارش تخلف',
			detail: 'گزارش شما ثبت شد و به‌زودی بررسی می‌شود.',
			life: 4000,
		})
	}

	constructor() {
		effect(() => {
			const c = this.query.data()
			if (!c) return
			const canonical = c.slug ? `/equipment/${c.id}-${c.slug}` : `/equipment/${c.id}`
			const price = c.minEffectivePrice ?? c.price
			const image = this.media()[0]
			this.seo.setSeo({
				title: `${c.title ?? 'تجهیزات'} | بازار تجهیزات بیوتی‌باس`,
				description: c.shortDescription ?? c.description?.slice(0, 160) ?? '',
				keywords: `خرید ${c.title ?? 'تجهیزات زیبایی'}, تجهیزات کلینیک, ${c.cityName ?? ''}`,
				canonicalPath: canonical,
				type: 'product',
				jsonLd: {
					type: 'Product',
					data: {
						name: c.title,
						description: c.shortDescription ?? undefined,
						...(image ? { image } : {}),
						...(this.brandModel() ? { brand: { '@type': 'Brand', name: this.brandModel() } } : {}),
						...(c.serialNumber && !c.serialPrivate ? { sku: c.serialNumber } : {}),
						// Offer فقط با قیمت معتبر و وقتی «تماس برای قیمت» نیست (PRD ch.13/27)
						...(c.contactForPrice !== true && price != null && price > 0 ?
							{
								offers: {
									'@type': 'Offer',
									price,
									priceCurrency: 'IRR',
									itemCondition: 'https://schema.org/UsedCondition',
									availability:
										c.inventoryCount == null || c.inventoryCount > 0 ?
											'https://schema.org/InStock'
										:	'https://schema.org/OutOfStock',
								},
							}
						:	{}),
					},
				},
			})
			this.seo.setSeo({
				title: `${c.title ?? 'تجهیزات'} | بازار تجهیزات بیوتی‌باس`,
				canonicalPath: canonical,
				jsonLd: {
					type: 'BreadcrumbList',
					data: {
						itemListElement: [
							{ '@type': 'ListItem', position: 1, name: 'بیوتی‌باس', item: `${SITE_BASE_URL}/` },
							{ '@type': 'ListItem', position: 2, name: 'تجهیزات', item: `${SITE_BASE_URL}/equipment` },
							{ '@type': 'ListItem', position: 3, name: c.title ?? 'آگهی', item: `${SITE_BASE_URL}${canonical}` },
						],
					},
				},
			})
		})
		effect(() => {
			if (this.query.isError()) this.seo.setNotFound()
		})
	}
}
